import { useCallback, useEffect, useRef, useState } from 'react'

const DEBOUNCE_MS = 400

interface IItem {
  id: number
  amountRequested: number
}

interface ISaveResult {
  errors?: any
  result?: any
}
type Save = (item: IItem, amount: number) => Promise<ISaveResult>
type Remove = (item: IItem) => void

// Keeps quantity drafts in the parent so a virtualized row unmounting on scroll
// cannot lose an unsaved edit; autosaves each draft on a debounce and lets the
// parent flush everything before submitting.
export const useAmountDrafts = (save: Save, remove?: Remove) => {
  const [drafts, setDrafts] = useState<Record<number, string>>({})
  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
  const pending = useRef<Record<number, Promise<ISaveResult>>>({})
  const itemsRef = useRef<Record<number, IItem>>({})
  const unresolved = useRef(false)

  useEffect(
    () => () => {
      Object.values(timers.current).forEach(clearTimeout)
    },
    [],
  )

  const cancelTimer = useCallback((id: number) => {
    if (!timers.current[id]) return
    clearTimeout(timers.current[id])
    delete timers.current[id]
  }, [])

  // Drop the draft so the input falls back to the saved amount.
  const clearDraft = useCallback(
    (id: number) => {
      cancelTimer(id)
      setDrafts((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    },
    [cancelTimer],
  )

  // An emptied or zeroed box. Only meaningful once the edit is committed —
  // mid-typing it is just a box on its way to a new number.
  const isCleared = useCallback((id: number) => {
    const draft = draftsRef.current[id]
    return draft !== undefined && (+draft || 0) === 0
  }, [])

  const persist = useCallback(
    (item: IItem) => {
      const draft = draftsRef.current[item.id]
      const amount = +(draft ?? item.amountRequested) || 0
      if (amount === item.amountRequested) return

      // A blank box coerces to 0, which the server treats as a deletion. Removing a
      // line is the trash control's job, so never autosave it.
      if (amount === 0) return

      const promise = save(item, amount).finally(() => {
        if (pending.current[item.id] === promise)
          delete pending.current[item.id]
      })
      pending.current[item.id] = promise
      return promise
    },
    [save],
  )

  const onAmountChange = useCallback(
    (item: IItem, value: string) => {
      itemsRef.current[item.id] = item
      unresolved.current = false
      setDrafts((prev) => ({ ...prev, [item.id]: value }))

      cancelTimer(item.id)
      timers.current[item.id] = setTimeout(() => {
        delete timers.current[item.id]
        persist(item)
      }, DEBOUNCE_MS)
    },
    [persist, cancelTimer],
  )

  // Save one item immediately (Enter / Tab / blur), cancelling its debounce.
  // Leaving the box empty asks to remove the line, which prompts for confirmation;
  // declining restores the saved amount so the row never shows a number it won't order.
  // Either answer holds back the next submit, so the order is never placed off the
  // back of a box the customer emptied.
  const flushItem = useCallback(
    (item: IItem) => {
      cancelTimer(item.id)

      if (isCleared(item.id)) {
        clearDraft(item.id)
        unresolved.current = true
        remove?.(item)
        return
      }

      persist(item)
    },
    [persist, cancelTimer, isCleared, clearDraft, remove],
  )

  // Cancel pending debounces, save every outstanding draft now, and wait for
  // all in-flight saves to settle. Returns true only if none reported an error
  // and no line was left without a quantity.
  const flush = useCallback(async () => {
    // Set when a box was emptied and resolved on blur; consumed here so the held
    // back submit is the next one only, not every one after it.
    let cleared = unresolved.current
    unresolved.current = false

    Object.keys(draftsRef.current).forEach((key) => {
      const id = +key
      if (!isCleared(id)) return

      // Restore the saved amount rather than ordering a quantity the row isn't showing.
      clearDraft(id)
      cleared = true
    })

    Object.entries(timers.current).forEach(([id, timer]) => {
      clearTimeout(timer)
      delete timers.current[+id]
      const item = itemsRef.current[+id]
      if (item) persist(item)
    })

    const results = await Promise.all(Object.values(pending.current))
    return !cleared && results.every((r) => !r?.errors)
  }, [persist, isCleared, clearDraft])

  return { drafts, onAmountChange, flushItem, flush, clearDraft }
}
